// Administration › Données des salariés (RGPD) : ce que la société enregistre sur ses salariés, et l'engagement de son représentant
// légal. Tout est appliqué par le SERVEUR (noms affichés, téléphone masqué, photo et temps refusés) : cet écran ne fait que régler.
// L'engagement est en ajout seul : chaque signature s'ajoute à l'historique, rien ne se modifie ni ne s'efface.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, PenLine, Settings2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { dateHeure } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, Champ, ChampChoix, Chargement, Dialogue, EtatErreur, Panneau, useNotifier } from '../../components/ui.tsx';
import { EXEMPLES_MODE_NOM, LIBELLES_MODE_NOM, type ModeNom, type ReglagesSalaries, type TexteEngagement } from '@shared/salaries.ts';

type Etat = 'absent' | 'demo' | 'a_renouveler' | 'a_jour';
interface Engagement {
  id: string; le: string; demo: boolean; version_texte: number; empreinte_texte: string; empreinte_reglages: string;
  signataire: { prenom: string; nom: string; qualite: string; qualite_libelle: string; precision: string | null };
  enregistre_par: string | null; adresse_ip: string | null; reglages: ReglagesSalaries; recapitulatif: { libelle: string; valeur: string }[];
}
interface EtatSalaries {
  reglages: ReglagesSalaries; reglages_par_defaut: boolean; modifies_le: string | null; recapitulatif: { libelle: string; valeur: string }[];
  empreinte_reglages: string; texte: TexteEngagement & { empreinte: string }; etat: Etat; dernier: Engagement | null; dernier_reel: Engagement | null;
  historique: Engagement[]; qualites: { code: string; libelle: string; a_preciser: boolean }[];
}

const CLE = ['salaries-reglages'];

/** Phrase du bandeau d'état (aussi utilisée par l'accueil). */
export function texteEtatEngagement(etat: Etat, le: string | null): { ton: 'ambre' | 'info' | 'succes'; titre: string; texte: string } {
  if (etat === 'absent') return { ton: 'ambre', titre: 'Engagement du représentant légal à signer',
    texte: 'Aucun engagement n’est enregistré : le représentant légal doit confirmer les réglages des données des salariés.' };
  if (etat === 'demo') return { ton: 'info', titre: 'Engagement fictif de démonstration',
    texte: 'Avant la mise en service, le représentant légal signe le véritable engagement.' };
  if (etat === 'a_renouveler') return { ton: 'ambre', titre: `Réglages modifiés depuis l’engagement du ${le ? dateHeure(le) : '…'}`,
    texte: 'Un nouvel engagement du représentant légal est demandé.' };
  return { ton: 'succes', titre: `Engagement à jour (signé le ${le ? dateHeure(le) : '…'})`, texte: 'Les réglages en vigueur sont ceux de l’engagement.' };
}

function Recapitulatif({ lignes }: { lignes: { libelle: string; valeur: string }[] }) {
  return (
    <dl className="fiche rgpd-recap">
      {lignes.map((l) => <div key={l.libelle}><dt>{l.libelle}</dt><dd>{l.valeur}</dd></div>)}
    </dl>
  );
}

function DialogueReglages({ e, fermer }: { e: EtatSalaries; fermer: () => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [r, setR] = useState<ReglagesSalaries>(e.reglages);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const enregistrer = async () => {
    setErreur(null); setEnvoi(true);
    try {
      const n = await api.put<EtatSalaries & { inchange: boolean }>('/api/organisation/salaries', r);
      qc.setQueryData(CLE, n);
      await qc.invalidateQueries({ queryKey: ['moi'] });
      notifier(n.inchange ? 'Réglages inchangés.' : n.etat === 'a_jour' ? 'Réglages enregistrés.' : 'Réglages enregistrés : un nouvel engagement du représentant légal est demandé.');
      fermer();
    } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
  };
  const ouiNon = (cle: 'telephoneCoequipiers' | 'photos' | 'temps', titre: string, oui: string, non: string, aide: string) => (
    <fieldset className="pile rgpd-choix">
      <legend className="champ-libelle">{titre}</legend>
      <label className="case"><input type="radio" name={cle} checked={r[cle]} onChange={() => setR({ ...r, [cle]: true })} /><span>{oui}</span></label>
      <label className="case"><input type="radio" name={cle} checked={!r[cle]} onChange={() => setR({ ...r, [cle]: false })} /><span>{non}</span></label>
      <span className="petit discret">{aide}</span>
    </fieldset>
  );
  return (
    <Dialogue titre="Réglages des données des salariés" ouvert fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} onClick={enregistrer}>Enregistrer</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <fieldset className="pile rgpd-choix">
          <legend className="champ-libelle">Nom des salariés montré aux autres utilisateurs</legend>
          {(Object.keys(LIBELLES_MODE_NOM) as ModeNom[]).map((mo) => (
            <label key={mo} className="case"><input type="radio" name="nom" checked={r.nom === mo} onChange={() => setR({ ...r, nom: mo })} />
              <span>{LIBELLES_MODE_NOM[mo]} <span className="petit discret">— « {EXEMPLES_MODE_NOM[mo]} »</span></span></label>
          ))}
          <span className="petit discret">Partout : Ma journée, planning, coches, page du jour, réserves, carnet, diagnostics. Chacun voit son propre nom complet ;
            ce qui est fait reste attribué au compte. Deux collègues de même prénom reçoivent la lettre qui les distingue.</span>
        </fieldset>
        {ouiNon('telephoneCoequipiers', 'Téléphone professionnel des techniciens', 'Visible de leurs coéquipiers', 'Visible de l’encadrement seulement',
          'L’encadrement (référent, chargé d’affaires, administrateur) voit toujours le numéro ; celui de l’encadrement reste visible de l’équipe pour joindre le responsable.')}
        {ouiNon('photos', 'Photos de chantier', 'Activées', 'Désactivées',
          'Désactivées : plus aucune photo n’est acceptée (même envoyée plus tard par un téléphone sans réseau) ; une étape « photo demandée » n’est plus bloquante ; les photos déjà prises restent.')}
        {ouiNon('temps', 'Déclaration du temps passé', 'Activée', 'Désactivée',
          'Suivi de chantier, pas le décompte légal du temps de travail. Désactivée : plus aucune déclaration ; les temps déjà déclarés restent visibles de l’encadrement.')}
        <Alerte ton="info">Changer un réglage demande un nouvel engagement du représentant légal.</Alerte>
      </div>
    </Dialogue>
  );
}

function DialogueEngagement({ e, fermer }: { e: EtatSalaries; fermer: () => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [qualite, setQualite] = useState('');
  const [precision, setPrecision] = useState('');
  const [accepte, setAccepte] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const aPreciser = e.qualites.find((q) => q.code === qualite)?.a_preciser ?? false;
  const pret = accepte && prenom.trim() && nom.trim() && qualite && (!aPreciser || precision.trim().length >= 3);
  const signer = async () => {
    setErreur(null); setEnvoi(true);
    try {
      const n = await api.post<EtatSalaries>('/api/organisation/salaries/engagements', { version: e.texte.version, empreinteReglages: e.empreinte_reglages,
        accepte, prenom, nom, qualite, precision: aPreciser ? precision : null });
      qc.setQueryData(CLE, n);
      await qc.invalidateQueries({ queryKey: ['moi'] });
      notifier('Engagement enregistré : il s’ajoute à l’historique.');
      fermer();
    } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Engagement du représentant légal" ouvert fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" icone={<PenLine aria-hidden />} chargement={envoi} disabled={!pret} onClick={signer}>Signer l’engagement</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <section className="pile" aria-label="Réglages engagés">
          <strong>Réglages engagés</strong>
          <Recapitulatif lignes={e.recapitulatif} />
        </section>
        <section className="pile rgpd-texte" aria-label="Texte de l’engagement">
          <strong>{e.texte.titre}</strong>
          <p style={{ margin: 0 }}>{e.texte.preambule}</p>
          <ol>{e.texte.declarations.map((d) => <li key={d}>{d}</li>)}</ol>
          <p className="petit secondaire" style={{ margin: 0 }}>{e.texte.portee}</p>
          <span className="tres-petit discret mono">Empreinte SHA-256 du texte : {e.texte.empreinte}</span>
        </section>
        <div className="grille-form">
          <Champ libelle="Prénom du signataire" value={prenom} onChange={(x) => setPrenom(x.target.value)} maxLength={100} autoComplete="given-name" />
          <Champ libelle="Nom du signataire" value={nom} onChange={(x) => setNom(x.target.value)} maxLength={100} autoComplete="family-name" />
          <ChampChoix libelle="Qualité" value={qualite} onChange={(x) => setQualite(x.target.value)} vide="Choisir…"
            options={e.qualites.map((q) => ({ valeur: q.code, libelle: q.libelle }))} />
          {aPreciser && <Champ libelle={qualite === 'delegataire' ? 'Délégation (donnée par qui, et quand)' : 'Précisez la qualité'} value={precision}
            onChange={(x) => setPrecision(x.target.value)} maxLength={300} />}
        </div>
        <label className="case rgpd-case">
          <input type="checkbox" checked={accepte} onChange={(x) => setAccepte(x.target.checked)} />
          <span>J’ai le pouvoir d’engager la société et je confirme, en son nom, les réglages, les déclarations et les engagements ci-dessus.</span>
        </label>
        <p className="tres-petit discret" style={{ margin: 0 }}>Enregistrés avec l’engagement : version et empreinte du texte, réglages et leur empreinte, signataire et
          qualité, votre compte, la date, l’heure et l’adresse IP. Un engagement ne se modifie ni ne s’efface.</p>
      </div>
    </Dialogue>
  );
}

export function OngletSalaries({ allerConservation }: { allerConservation?: () => void } = {}) {
  const q = useQuery({ queryKey: CLE, queryFn: () => api.get<EtatSalaries>('/api/organisation/salaries') });
  const [dialogue, setDialogue] = useState<null | 'reglages' | 'engagement'>(null);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const e = q.data!;
  const bandeau = texteEtatEngagement(e.etat, e.dernier?.le ?? null);
  const aSigner = e.etat !== 'a_jour';
  return (
    <div className="pile">
      <Panneau titre="Données des salariés (RGPD)">
        <div className="pile">
          <Alerte ton={bandeau.ton} titre={bandeau.titre}>{bandeau.texte}</Alerte>
          <div className="barre-actions">
            {aSigner && <Bouton variante="principal" icone={<PenLine aria-hidden />} onClick={() => setDialogue('engagement')}>
              {e.etat === 'a_renouveler' ? 'Signer un nouvel engagement' : 'Signer l’engagement'}</Bouton>}
            <Bouton variante={aSigner ? 'secondaire' : 'principal'} icone={<Settings2 aria-hidden />} onClick={() => setDialogue('reglages')}>Modifier les réglages</Bouton>
            <a className="btn" href="/api/organisation/salaries/note-information.pdf" target="_blank" rel="noopener"><FileText aria-hidden />Note aux salariés (PDF)</a>
          </div>
          <Recapitulatif lignes={e.recapitulatif} />
          <p className="petit discret" style={{ margin: 0 }}>
            {e.reglages_par_defaut ? 'Valeurs par défaut de TRAME (les plus protectrices compatibles avec le terrain) : jamais modifiées par la société.'
              : `Réglages modifiés le ${e.modifies_le ? dateHeure(e.modifies_le) : '…'}.`} Appliqués par le serveur, pour toute la société.</p>
          <p className="petit discret" style={{ margin: 0 }}>Durées de conservation (historique et photos, temps passé, traces de connexion, comptes désactivés) :
            {allerConservation ? <> <a href="/administration?onglet=conservation" onClick={(x) => { x.preventDefault(); allerConservation(); }}>Conservation des données</a>.</> : ' onglet « Conservation des données ».'}
            {' '}La note aux salariés reprend les durées réglées.</p>
        </div>
      </Panneau>
      <Panneau serre titre={`Historique des engagements (${e.historique.length})`}>
        {e.historique.length === 0 ? <p className="petit secondaire" style={{ margin: 'var(--e-4) var(--e-5)' }}>Aucun engagement enregistré.</p> : (
          <div className="liste">
            {e.historique.map((x, i) => (
              <div key={x.id} className="liste-element" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <span className="liste-element-corps" style={{ minWidth: 220 }}>
                  <span className="liste-element-titre">{x.signataire.prenom} {x.signataire.nom}
                    <span className="petit discret"> — {x.signataire.qualite_libelle}{x.signataire.precision ? ` (${x.signataire.precision})` : ''}</span></span>
                  <span className="petit secondaire">Le {dateHeure(x.le)} — texte version {x.version_texte} — enregistré par {x.enregistre_par ?? 'compte inconnu'}
                    {x.adresse_ip ? ` (adresse ${x.adresse_ip})` : ''}</span>
                  <details className="petit">
                    <summary>Réglages engagés</summary>
                    <Recapitulatif lignes={x.recapitulatif} />
                    <span className="tres-petit discret mono rgpd-empreinte">Empreinte des réglages : {x.empreinte_reglages}</span>
                  </details>
                </span>
                {x.demo && <Badge ton="ambre">Fictif (démo)</Badge>}
                {i === 0 && e.etat === 'a_jour' && <Badge ton="succes">En vigueur</Badge>}
                {i === 0 && e.etat === 'a_renouveler' && <Badge ton="ambre">Réglages modifiés depuis</Badge>}
              </div>
            ))}
          </div>
        )}
      </Panneau>
      {dialogue === 'reglages' && <DialogueReglages e={e} fermer={() => setDialogue(null)} />}
      {dialogue === 'engagement' && <DialogueEngagement e={e} fermer={() => setDialogue(null)} />}
    </div>
  );
}
